import React, { useState } from "react"

import {
	Grid,
	IconButton,
	TextField,
	Tooltip,
	Typography,
	CircularProgress,
	InputAdornment
} from "@material-ui/core"

import {
	Search as SearchIcon,
	InfoOutlined as InfoOutlinedIcon,
	FileCopyOutlined as FileCopyOutlinedIcon,
	Launch as LaunchIcon
} from "@material-ui/icons"

import { InboxAdminApi } from "@/services/Api"
import useValidation, { ErrorType } from "@/hooks/useValidation"
import { Notification } from "@/components"
import useDebounce from "@/hooks/useDebounce"
import { Link } from "react-router-dom"
import { getStatusCode } from "@/utils/response"
import { AxiosError } from "axios"

type WebhookSearchResult = {
	instanceId: number
	type: "bot" | "integration"
	link: string
	name: string
}

const SearchForIntegrationsAndBotsByWebhookUrlToolPage = () => {
	const [webhookUrlOrHash, setWebhookUrlOrHash] = useState("")
	const [loading, setLoading] = useState(false)
	const [searchResult, setSearchResult] = useState<WebhookSearchResult | null>(null)
	const { validation, triggerValidation, clearValidation } = useValidation()

	const typeLabels: Record<WebhookSearchResult["type"], string> = {
		bot: "Bot",
		integration: "Integração"
	}

	const hashMessageInfo = "A HASH é a parte final da URL do webhook. Por exemplo: em https://webhook.letalk.com.br/abc-1234-5678, o HASH é 'abc-1234-5678'."

	const handleCopyToClipboard = (text: string, label: string) => {
		navigator.clipboard.writeText(text)
		Notification.info({ message: `${label} copiado para a área de transferência!` })
	}

	const handleSearchInstanceDataByWebhookUrlOrHash = async (searchValue?: string) => {
		const valueToSearch = searchValue || webhookUrlOrHash

		if (!valueToSearch.trim()) {
			setSearchResult(null)
			return
		}

		setLoading(true)
		clearValidation("webhookUrlOrHash")
		setSearchResult(null)

		try {
			const response = await InboxAdminApi.get<{ data: WebhookSearchResult }>(
				"/webhook",
				{
					params: {
						webhookUrlOrHash: valueToSearch
					}
				}
			)

			if (response.data?.data) {
				setSearchResult(response.data.data)
			}
		} catch (error) {
			const responseStatusCode = getStatusCode(error as AxiosError)
			const webhookNotFound = responseStatusCode === 404

			if (webhookNotFound) {
				Notification.warning({ message: "Nenhum bot ou integração encontrado para este webhook." })
			} else {
				triggerValidation(error as ErrorType)
			}
		} finally {
			setLoading(false)
		}
	}

	useDebounce(
		async () => await handleSearchInstanceDataByWebhookUrlOrHash(webhookUrlOrHash),
		webhookUrlOrHash,
		1000
	)

	return (
		<Grid container
			direction="column"
			spacing={2}
		>
			<Grid item>
				<Grid container
					direction="column"
					spacing={1}
				>
					<Grid item>
						<Typography color="textPrimary" variant="h4">
							Buscar dados de integrações ou bots por URL de webhook
						</Typography>
					</Grid>

					<Grid item>
						<Grid container
							alignItems="center"
							spacing={1}
						>
							<Grid item>
								<Typography color="textSecondary" variant="subtitle1">
									Informe a <strong>URL</strong> ou <strong>HASH</strong> do webhook para buscar os dados do ID da instância e o link do bot/integração.
								</Typography>
							</Grid>

							<Grid item>
								<Tooltip
									title={hashMessageInfo}
									placement="right"
								>
									<InfoOutlinedIcon fontSize="small" color="action"
									/>
								</Tooltip>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			<Grid item>
				<Grid container
					alignItems="center"
					spacing={1}
				>
					<Grid item xs={7}>
						<TextField
							id="input-webhook-url-or-hash"
							name="webhookUrlOrHash"
							variant="outlined"
							placeholder="Pesquisar"
							fullWidth
							value={webhookUrlOrHash}
							onChange={(e) => {
								setWebhookUrlOrHash(e.target.value)
								clearValidation("webhookUrlOrHash")
							}}
							error={!!validation.webhookUrlOrHash}
							helperText={validation.webhookUrlOrHash}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon />
									</InputAdornment>
								),
								endAdornment: loading && (
									<InputAdornment position="end">
										<CircularProgress size={20} />
									</InputAdornment>
								)
							}}
						/>
					</Grid>
				</Grid>

				{validation.message && (
					<Grid item xs={12}>
						<Typography color="error" variant="caption">
							{validation.message}
						</Typography>
					</Grid>
				)}
			</Grid>

			{searchResult && (
				<Grid item>
					<Grid container direction="column" spacing={2}>
						<Grid item>
							<Typography color="textPrimary" variant="h4">
                                Dados encontrados:
							</Typography>
						</Grid>

						<Grid item xs={12} sm={9} md={7}>
							<TextField
								label="Tipo"
								variant="outlined"
								fullWidth
								value={typeLabels[searchResult.type]}
								InputProps={{
									readOnly: true
								}}
							/>
						</Grid>

						<Grid item xs={12} sm={9} md={7}>
							<TextField
								label="ID da Instância"
								variant="outlined"
								fullWidth
								value={searchResult.instanceId}
								InputProps={{
									readOnly: true,
									endAdornment: (
										<InputAdornment position="end">
											<Tooltip title="Copiar ID da instância">
												<IconButton
													onClick={() => handleCopyToClipboard(searchResult.instanceId.toString(), "ID da Instância")}
													edge="end"
												>
													<FileCopyOutlinedIcon fontSize="small" />
												</IconButton>
											</Tooltip>
											<Tooltip title="Acessar página da instância">
												<Link
													target="_blank"
													rel="noopener noreferrer"
													to={`/inbox/instance/${searchResult.instanceId}`}
												>
													<IconButton
														edge="end"
													>
														<LaunchIcon fontSize="small" />
													</IconButton>
												</Link>
											</Tooltip>
										</InputAdornment>
									)
								}}
							/>
						</Grid>

						<Grid item xs={12} sm={9} md={7}>
							<TextField
								label="Nome"
								variant="outlined"
								fullWidth
								value={searchResult.name}
								InputProps={{
									readOnly: true,
									endAdornment: (
										<InputAdornment position="end">
											<Tooltip title="Copiar nome">
												<IconButton
													onClick={() => handleCopyToClipboard(searchResult.name, "Nome")}
													edge="end"
												>
													<FileCopyOutlinedIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</InputAdornment>
									)
								}}
							/>
						</Grid>

						<Grid item xs={12} sm={9} md={7}>
							<TextField
								label="Link"
								variant="outlined"
								fullWidth
								value={searchResult.link}
								InputProps={{
									readOnly: true,
									endAdornment: (
										<InputAdornment position="end">
											<Tooltip title="Copiar link">
												<IconButton
													onClick={() => handleCopyToClipboard(searchResult.link, "Link")}
													edge="end"
												>
													<FileCopyOutlinedIcon fontSize="small" />
												</IconButton>
											</Tooltip>
											<Tooltip title="Abrir link em nova aba">
												<IconButton
													href={searchResult.link}
													target="_blank"
													rel="noopener noreferrer"
													edge="end"
												>
													<LaunchIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</InputAdornment>
									)
								}}
							/>
						</Grid>
					</Grid>
				</Grid>
			)}
		</Grid>
	)
}

export default SearchForIntegrationsAndBotsByWebhookUrlToolPage
